import type { ParameterDefinitionCreateOperation } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, dateUtils, notEmpty } from '@festina-life/core'
import { Alert, FullscreenSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useMemo } from 'react'

import { StackableTable } from '../../../components/stackableTable/StackableTable'
import type {
  StackableType,
  ValidStackableRatios,
} from '../../../components/stackableTable/types'
import { useConfigurationContext } from '../../../context/configurationContext'
import { useParameterContext } from '../../../context/parameterContext'
import { useParameterDefinitionId } from '../../../hooks/useParameterDefinitionId'
import { useApplicationViewOperationsContext } from '../../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { useNavigateToCurrentParameterDefinitionPath } from '../useNavigateToCurrentParameterDefinitionPath'
import { toStackableType } from './helpers'
import { ParameterDimensionRowCellSelector } from './ParameterDimensionRowCellSelector'
import { ParameterRowCell } from './ParameterRowCell'
import { ParameterValues } from './ParameterValues'
import { ParameterValuesHeader } from './ParameterValuesHeader'
import {
  AddParameterDefinitionDimensionPlaceholder,
  isAddParameterDefinitionDimensionPlaceholder,
  type ParameterDimensionDefinitionRowData,
} from './types'

const PARAMETER_HEADER_COLUMN_RATIOS = [1, 1] as ValidStackableRatios[]

export const ParameterViewer = () => {
  const {
    parameterDefinitionOperation,
    parameterDefinitionErrors,
    parameterValueEntityOperationsForView,
    amendOperationsInView,
    viewingDetails,
  } = useParameterContext()

  const { viewAt } = useConfigurationContext()
  const translate = useTranslate()
  const { onAddOrUpdateOperation, isLoading } = useOperationsContext()
  const parameterDefinitionId = useParameterDefinitionId()
  const navigateToPath = useNavigateToCurrentParameterDefinitionPath()
  const { applicationParameterDefinitions } =
    useApplicationViewOperationsContext()

  const allDefinitionsForIdMap = applicationParameterDefinitions?.get(
    parameterDefinitionId,
  )

  const parameterDefinitionEntity =
    parameterDefinitionOperation?.internalOperation.entity

  const allDefinitionEntities = useMemo(
    () =>
      allDefinitionsForIdMap?.operations
        .map((entry) => entry.internalOperation.entity)
        .filter(notEmpty),
    [allDefinitionsForIdMap?.operations],
  )

  const allTableRows = useMemo(() => {
    const dataRows = (
      parameterDefinitionOperation?.internalOperation.entity
        ?.dimensionDefinitions ?? []
    )
      .filter(notEmpty)
      .map((dimensionDefinition) =>
        toStackableType(dimensionDefinition, viewingDetails),
      )

    if (parameterDefinitionOperation?.isDraft) {
      dataRows.push(
        toStackableType(
          AddParameterDefinitionDimensionPlaceholder,
          viewingDetails,
        ),
      )
    }

    return dataRows
  }, [
    parameterDefinitionOperation?.internalOperation.entity
      ?.dimensionDefinitions,
    parameterDefinitionOperation?.isDraft,
    viewingDetails,
  ])

  const handleRemoveDimension = async (
    stackableDimension: StackableType<ParameterDimensionDefinitionRowData>,
  ) => {
    if (
      stackableDimension.value !== AddParameterDefinitionDimensionPlaceholder
    ) {
      assertNotEmpty(
        parameterDefinitionOperation?.internalOperation.entity
          ?.dimensionDefinitions,
      )

      const typedValue = stackableDimension.value
      const nextDimensions =
        parameterDefinitionOperation?.internalOperation.entity?.dimensionDefinitions?.filter(
          (definition) => definition !== typedValue,
        )

      const nextParameterDefinitionCreateOperation = {
        ...parameterDefinitionOperation?.internalOperation,
        entity: {
          ...parameterDefinitionOperation?.internalOperation.entity,
          dimensionDefinitions: nextDimensions,
        },
      } satisfies ParameterDefinitionCreateOperation

      await onAddOrUpdateOperation({
        operation: nextParameterDefinitionCreateOperation,
        layer: parameterDefinitionOperation.layer,
        block: parameterDefinitionOperation.block,
        // Irrelevant, you can only remove a dimension from a draft, which can only exist in the workspace.
        shouldOpenUnifiedBranchWorkspace: true,
      })
    }
  }

  const isRenderingAmendments = !!amendOperationsInView.length
  const isRenderingValues = !!parameterValueEntityOperationsForView?.length

  const renderedDefinition = {
    value: parameterDefinitionOperation,
    key: () => parameterDefinitionId,
    columns: 3,
    ratios: [1, 1, 1],
    errors: [],
    isActive: viewingDetails === parameterDefinitionEntity,
  } satisfies StackableType<typeof parameterDefinitionOperation>

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  const isEmpty =
    !parameterDefinitionEntity && !isRenderingValues && !isRenderingAmendments

  return isEmpty ? (
    <div className="flex size-full items-center justify-center">
      <Alert
        severity="warning"
        title={translate('warning.noDataForVersion', {
          viewAt: dateUtils.dateAsStr(viewAt),
          nextViewAtDate: dateUtils.dateAsStr(
            allDefinitionEntities?.at(0)?.fromDate,
          ),
        })}
      />
    </div>
  ) : (
    <div
      className={cx('h-full overflow-y-auto p-4 divide-y divide-gray-200', {
        'pr-16': !!parameterDefinitionOperation?.isDraft,
      })}
    >
      <div className="mb-8">
        {Array.from(parameterDefinitionErrors.values()).map(({ error }) => (
          <Alert
            key={`${error.operationKey}-${error.errorMessage}`}
            severity="error"
            title={`${error.operationName}: ${error.errorMessage}`}
            className="my-2"
          />
        ))}
        <StackableTable
          indentationStep={0}
          parentCell={ParameterRowCell}
          parent={renderedDefinition}
          rowClassName="min-h-12 overflow-hidden"
          emptyRowColumnRatios={PARAMETER_HEADER_COLUMN_RATIOS}
          headers={[
            translate('parameterViewer.header.name'),
            translate('parameterViewer.header.type'),
            translate('parameterViewer.header.environmentType'),
          ]}
          onParentClicked={() => navigateToPath(parameterDefinitionId)}
        >
          <StackableTable
            headers={[
              translate('parameterViewer.dimensionsHeader.dimension'),
              translate('parameterViewer.dimensionsHeader.type'),
              translate('parameterViewer.dimensionsHeader.dimensionExpression'),
            ]}
            indentationStep={1}
            childCell={ParameterDimensionRowCellSelector}
            data={allTableRows}
            rowClassName="min-h-12 overflow-hidden"
            emptyRowColumnRatios={[1, 1]}
            childActions={[
              {
                actionId: 'removeDefinition',
                node: renderedDefinition.value?.isDraft ? (
                  <Icon
                    name="MinusCircle"
                    size="md"
                    className="cursor-pointer opacity-80 hover:opacity-100 dark:text-gray-200"
                    color="unset"
                  />
                ) : null,
                action: handleRemoveDimension,
              },
            ]}
            onChildClicked={(childItem) =>
              !isAddParameterDefinitionDimensionPlaceholder(childItem.value)
                ? navigateToPath(
                    `${parameterDefinitionId}.${childItem.value.name}`,
                  )
                : undefined
            }
          />
        </StackableTable>
      </div>
      <div className="flex flex-col gap-4 pb-12">
        <ParameterValuesHeader />
        <ParameterValues />
      </div>
    </div>
  )
}
